import { useSource } from "@/components/canvas/useSource.ts";
import type { SourceDetailResponse } from "@/types/sources.ts";

/**
 * The canonical detail-page path for a source subtype, or null when the subtype
 * has no page yet.
 *
 * Both renderable subtypes address the ent by the same id `/sources/:id` carries,
 * so this is a pure relabeling — it's what makes `/sources/:id` and the
 * type-keyed paths interchangeable for the navigation boundaries.
 */
export const sourcePagePath = (source: SourceDetailResponse, id: string): string | null => {
  switch (source.type) {
    case "communication":
      return `/communications/${id}`;
    case "event":
      return `/calendar/event/${id}`;
    default:
      return null;
  }
};

/**
 * Resolve a bare source ent id to the in-app detail route for its subtype,
 * backing the full-page `/sources/:id` redirect.
 *
 * Reads the subtype off `GET /a/sources/:id` — the same query the canvas uses, so
 * React Query serves one request for both — rather than the older
 * `/sources/:id/route` endpoint, which only ever resolved communications and left
 * every event link dead-ending on "can't be viewed yet".
 */
export const useSourceRoute = ({ id }: { id?: string }) => {
  const { data, isLoading, error } = useSource(id);
  return {
    route: data && id ? sourcePagePath(data, id) : null,
    /** The resolved source, for subtypes read in place rather than redirected. */
    data,
    isLoading,
    error,
  };
};
